import i18n from "@plugins/i18n.ts";
import DOMUtils from "@utils/dom.utils";
import { isEmpty } from "@/utils/var.check";
import DateTimeFormatter from "@utils/datetime.formatter";
import type { MediaObjectCopyRightModel, MediaObjectFormData } from "@plugins/media/types.ts";
import {
  ALL_OPTION,
  AVAILABILITY,
  DOWNLOADABLE_RIGHT,
  LICENSE_TYPE,
  MEDIA_FILE_STATUS,
  MEDIA_TYPE,
  MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION,
  MEDIA_OBJECT_COPYRIGHT_TERRITORY,
  MODIFICATION_RIGHT,
  MOE_COPYRIGHT_AUTHOR,
  PRINTABLE_RIGHT,
} from "@plugins/media/media.constant";

export function isMediaObjectFileReady(
  fileStatus: string | undefined,
  fileType: string | undefined,
): boolean {
  const normalizedStatus = fileStatus?.toUpperCase();
  const normalizedType = fileType?.toUpperCase();
  return (
    normalizedStatus === MEDIA_FILE_STATUS.READY ||
    (normalizedType === MEDIA_TYPE.VIDEO &&
      normalizedStatus === MEDIA_FILE_STATUS.TRANSCODING_ERROR)
  );
}

interface BuildIpRequestDataOptions {
  excludeResourceType?: boolean;
}

export default {
  // From seconds to HH:mm:ss
  formatTime: function (time) {
    if (!time) {
      return this.formatNumber(0, false) + ":" + this.formatNumber(0, false);
    }
    const hour = Math.floor(time / 3600);
    const minute = Math.floor((time - hour * 3600) / 60);
    const second = Math.floor(time - hour * 3600 - minute * 60);
    if (hour > 0) {
      return (
        this.formatNumber(hour, true) +
        ":" +
        this.formatNumber(minute, false) +
        ":" +
        this.formatNumber(second, false)
      );
    } else {
      return this.formatNumber(minute, false) + ":" + this.formatNumber(second, false);
    }
  },
  // From HH:mm:ss to seconds
  deformatTime: function (timeString) {
    const timeArray = timeString.split(":");
    let time = 0;
    if (timeArray.length === 3) {
      time = timeArray[0] * 3600 + timeArray[1] * 60 + timeArray[2] * 1;
    } else {
      time = timeArray[0] * 60 + timeArray[1] * 1;
    }
    return time;
  },
  formatNumber: function (number, isHour) {
    if (number < 10 && !isHour) {
      return "0" + number;
    } else {
      return number;
    }
  },
  // From seconds to HH:MM:SS.MILLISECONDS
  // eg 67.051secs -> 00:01:07.051
  formatToVttTimestamps(seconds) {
    let milliseconds = seconds * 1000;
    seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    milliseconds = milliseconds % 1000;
    seconds = seconds % 60;
    minutes = minutes % 60;
    return (
      (hours < 10 ? "0" : "") +
      hours +
      ":" +
      (minutes < 10 ? "0" : "") +
      minutes +
      ":" +
      (seconds < 10 ? "0" : "") +
      seconds +
      "." +
      (milliseconds < 100 ? "0" : "") +
      (milliseconds < 10 ? "0" : "") +
      milliseconds
    );
  },
  timeSettingsValidate(error, startInput, endInput, duration) {
    if (this.timeFormatValidate(startInput) || this.timeFormatValidate(endInput)) {
      error.message = i18n.t("media_object_settings_time_format_invalid");
      return;
    }
    this.timeDurationValidate(error, startInput, endInput, duration);
  },
  timeFormatValidate(timeInput) {
    let hasError = false;
    const regexp = /^(\d+:)?([0-5]\d):([0-5]\d)$/;
    const checkTime = regexp.test(timeInput);
    if (!checkTime) {
      hasError = true;
    }
    return hasError;
  },
  timeDurationValidate(error, startInput, endInput, duration) {
    const start = this.deformatTime(startInput);
    const end = this.deformatTime(endInput);
    const validNumber = !isNaN(start) && !isNaN(end);

    if (validNumber) {
      // start time  must be within media duration
      if (start >= duration) {
        error.message = i18n.t("media_object_settings_time_duration_invalid");
        error.cueStartError = true;
        return;
      }

      // start time must be within media duration
      if (end > duration) {
        error.message = i18n.t("media_object_settings_time_duration_invalid");
        error.cueEndError = true;
        return;
      }

      // end time must be later than start time
      if (start >= end) {
        error.message = i18n.t("media_object_settings_start_and_end_time_invalid");
      }
    }
  },
  getTextAreaDescription(desc) {
    const placeholder = DOMUtils.createElement("div");
    placeholder.innerHTML = desc;

    if (!placeholder.querySelector(".mo-description .paragraph")) {
      return desc?.trim() ?? ""; // Return the plain text
    }

    let text = "";
    const textParagraphs = DOMUtils.elOf(placeholder, ".mo-description .paragraph");
    textParagraphs.forEach((paragraph, index) => {
      if (!isEmpty(paragraph.innerText)) {
        text += paragraph.innerText;
      }
      // Only add newline if it's not the last paragraph
      if (index < textParagraphs.length - 1) {
        text += "\n";
      }
    });
    return text;
  },
  /**
   * Format seconds to mm:ss
   * @param {Number} seconds
   * @returns {String} mm:ss
   */
  formatToVideoTimestamp(seconds) {
    const minutes = Math.floor(seconds / 60);
    seconds = Math.round(seconds % 60);
    return (minutes < 10 ? "0" : "") + minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
  },
  /**
   * Default value for media ip details
   */
  createBlankIpFormData(): MediaObjectFormData {
    return {
      slsUse: false,
      owner: "",
      copyRightAuthor: "",
      authorAcknowledgement: "",
      author: "",
      authorAttribution: "",
      rmsType: "",
      licenseType: "",
      exceptionDetails: "",
      startDate: "",
      endDate: "",
      territory: "",
      singleUseLicense: false,
      reproduction: "",
      downloadableRight: "",
      printableRight: "",
      modificationRight: "",
      modificationType: [],
      modificationDetails: "",
      otherTerms: "",
      licenseFee: "",
      licensorName: "",
      licensorContact: "",
      licenseSource: "",
      licensorRequest: "",
      docRecord: "",
      location: "",
      backgroundIP: "",
      availability: "",
      levelAndStreams: [],
      subjects: [],
    };
  },
  createMoeOwnedIpFields(): Partial<MediaObjectFormData> {
    return {
      copyRightAuthor: MOE_COPYRIGHT_AUTHOR,
      authorAcknowledgement: `© ${MOE_COPYRIGHT_AUTHOR}`,
      author: MOE_COPYRIGHT_AUTHOR,
      authorAttribution: `Author/Creator: ${MOE_COPYRIGHT_AUTHOR}`,
      licenseType: LICENSE_TYPE.PERPETUAL.value,
      territory: MEDIA_OBJECT_COPYRIGHT_TERRITORY.WORLDWIDE.value,
      downloadableRight: DOWNLOADABLE_RIGHT.YES.value,
      printableRight: PRINTABLE_RIGHT.YES.value,
      modificationRight: MODIFICATION_RIGHT.YES.value,
      availability: AVAILABILITY.UNRESTRICTED.value,
    };
  },
  buildIpRequestData(
    formData: MediaObjectFormData,
    id: number,
    options: BuildIpRequestDataOptions = {},
  ): MediaObjectCopyRightModel {
    const requestData = {};
    Object.keys(formData).forEach((key) => {
      if (options.excludeResourceType && key === "rmsType") {
        return;
      }

      const value = formData[key];
      if (isEmpty(value)) {
        if (key === "levelAndStreams" || key === "subjects") {
          requestData[key] = [];
        } else if (key === "singleUseLicense") {
          requestData[key] = false;
        } else {
          requestData[key] = null;
        }
      } else {
        requestData[key] = value;
        if (key === "startDate" || key === "endDate") {
          requestData[key] = DateTimeFormatter.formatToLocalDateTime(value);
        } else if (key === "levelAndStreams" || key === "subjects") {
          if (Array.isArray(requestData[key]) && typeof requestData[key][0] === "string") {
            requestData[key] = requestData[key]
              .filter((item) => item !== ALL_OPTION.value)
              .map((item) => ({ id: item }));
          }
        }
      }
    });
    requestData["id"] = id;
    return requestData as MediaObjectCopyRightModel;
  },
  createBulkEditIpFormData(): MediaObjectFormData {
    return {
      ...this.createBlankIpFormData(),
      ...this.createMoeOwnedIpFields(),
      owner: MEDIA_OBJECT_COPYRIGHT_OWNER_OPTION.MOE_OWNED.value,
      slsUse: true,
    };
  },

  resolveTranscribeActivityId(pageId: number | string | null | undefined): number {
    const activityId = Number(pageId);
    return Number.isInteger(activityId) ? activityId : 0;
  },
};
